import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { BaseQueryParamService } from '../../../shared/services/base-query-param.service';
import { BaseRolesService } from '../../../core/services/base-roles.service';
import {
  CUSTOMER_PLANNING_BOARD_FILTERS,
  PLANNING_BOARD_FILTER_QUERY_KEYS,
  PSP_PLANNING_BOARD_FILTERS,
} from '../_constants/base-planning-board-filters.constant';
import { BasePlanningBoardListPayloadInterface } from '../_interfaces/base-planning-board-list-payload.interface';
import { PlanningBoardFilterConfig } from '../_interfaces/base-planning-board-filter-config.interface';
import { PlanningBoardQueries } from '../_enums/planning-board-queries.enum';

@Injectable({ providedIn: 'root' })
export class BasePlanningBoardFiltersService {
  constructor(private queryParamService: BaseQueryParamService, private rolesService: BaseRolesService) {}

  getRoleFilters(): ReadonlyArray<PlanningBoardFilterConfig> {
    return this.rolesService.isPsp() ? PSP_PLANNING_BOARD_FILTERS : CUSTOMER_PLANNING_BOARD_FILTERS;
  }

  // Resolves the option list a filter should render from the backend filter-lists payload.
  // Object facets are used directly; enum facets (`staticOptions`) arrive as a flat string[]
  // of present values and are intersected with the fixed, translatable option objects.
  resolveOptions(config: PlanningBoardFilterConfig, filters: unknown): Array<Record<string, unknown>> {
    const facet = (filters as Record<string, Array<unknown>> | undefined)?.[config.filtersField] ?? [];
    if (config.staticOptions) {
      const present = new Set(facet as Array<string>);
      return config.staticOptions.filter((option) => present.has(option.id)) as unknown as Array<
        Record<string, unknown>
      >;
    }
    return facet as Array<Record<string, unknown>>;
  }

  setSelection(queryKey: string, ids: ReadonlyArray<number | string>): void {
    const value = ids && ids.length > 0 ? ids.join(',') : null;
    this.queryParamService.updateQueryParams({
      [queryKey]: value,
      [PlanningBoardQueries.Page]: null,
    });
  }

  // Numeric (id-based) filters parse back to numbers; enum filters (e.g. `entryTypes`)
  // carry non-numeric string values, which are preserved as-is.
  parseSelection(value: string | null | undefined): Array<number | string> {
    if (!value) {
      return [];
    }
    return value.split(',').map((v) => {
      const numeric = Number(v);
      return Number.isNaN(numeric) ? v : numeric;
    });
  }

  /**
   * Extract only the filter-selection query params (excluding view/date/order/page),
   * used to remember and restore the board's active filters across navigation.
   */
  pickFilterParams(params: Params): Params {
    const result: Params = {};
    PLANNING_BOARD_FILTER_QUERY_KEYS.forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== '') {
        result[key] = value;
      }
    });
    return result;
  }

  toApiPayload(params: Params): BasePlanningBoardListPayloadInterface {
    const payload: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
      if (this.isPresentationOnly(key)) {
        continue;
      }
      if (key === PlanningBoardQueries.Order) {
        Object.assign(payload, this.splitSorting(value));
        continue;
      }
      payload[this.toApiKey(key)] = value;
    }

    return payload as unknown as BasePlanningBoardListPayloadInterface;
  }

  private isPresentationOnly(key: string): boolean {
    return key === PlanningBoardQueries.View;
  }

  private splitSorting(value: unknown): Record<string, string> {
    const [key, direction] = String(value).split(',');
    const result: Record<string, string> = {};
    if (key) {
      result.orderKey = key;
    }
    if (direction) {
      result.orderDirection = direction;
    }
    return result;
  }

  private toApiKey(key: string): string {
    const isFilterKey = PLANNING_BOARD_FILTER_QUERY_KEYS.includes(key);
    return isFilterKey ? `${key}[]` : key;
  }
}
